import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import type { Artwork } from '@/types';
import { useTranslation } from '@/hooks/useTranslation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  artwork: Artwork;
}

// Three templates: horizontal (landscape), vertical (portrait), blank (centered on bare wall)
// Auto-select based on artwork dimensions; blank used for specific test artworks only
const TEMPLATES = {
  horizontal: {
    src: '/view-in-room-template.jpg',
    width: 1280,
    height: 800,
    frame: { leftPct: 25.4, topPct: 13.4, widthPct: 51.4, heightPct: 42.6 },
    fit: 'cover' as const,
  },
  vertical: {
    src: '/view-in-room-vertical.jpg',
    width: 1600,
    height: 1066,
    frame: { leftPct: 42.0, topPct: 32.0, widthPct: 16.7, heightPct: 38.5 },
    fit: 'cover' as const,
  },
  blank: {
    src: '/view-in-room-blank.jpg',
    width: 1600,
    height: 1066,
    // Used as fallback if no artwork.dimensions present
    frame: { leftPct: 26.0, topPct: 18.0, widthPct: 42.0, heightPct: 56.0 },
    fit: 'contain' as const,
    // Real-world calibration: tune pxPerCm so a known reference (e.g. a 60×80 cm canvas)
    // visually matches the wall's perceived scale. wallArea bounds where to center the art.
    realScale: {
      pxPerCm: 6.0,
      wallArea: { leftPct: 13, topPct: 5, widthPct: 62, heightPct: 80 },
    },
  },
};

// Slugs that use the new blank-wall template (test set)
const BLANK_TEMPLATE_SLUGS = new Set(['untitled-63', 'untitled-58', 'untitled-70']);

const ViewInRoomModal: React.FC<Props> = ({ isOpen, onClose, artwork }) => {
  const { t, localize } = useTranslation();

  // Detect orientation from actual image aspect ratio (more reliable than admin-entered dimensions)
  const [imgAspect, setImgAspect] = useState<number | null>(null);

  useEffect(() => {
    const url = artwork.images?.[0];
    if (!url) return;
    const im = new Image();
    im.onload = () => setImgAspect(im.naturalWidth / im.naturalHeight);
    im.src = url;
  }, [artwork.images]);

  // Fallback to dimensions if image hasn't loaded yet
  const dimW = artwork.dimensions?.width || 0;
  const dimH = artwork.dimensions?.height || 0;
  const dimAspect = dimW > 0 && dimH > 0 ? dimW / dimH : null;
  const aspect = imgAspect ?? dimAspect ?? 1;
  // Portrait if height > width × 1.05 (i.e. aspect < 0.95)
  const isPortrait = aspect < 0.95;
  const useBlank = BLANK_TEMPLATE_SLUGS.has(artwork.slug || '');
  const template = useBlank
    ? TEMPLATES.blank
    : (isPortrait ? TEMPLATES.vertical : TEMPLATES.horizontal);
  const TEMPLATE_W = template.width;
  const TEMPLATE_H = template.height;

  // Compute the inner frame box. For the blank template, scale by real-world cm dimensions.
  const FRAME_INNER = ((): { leftPct: number; topPct: number; widthPct: number; heightPct: number } => {
    if (useBlank && 'realScale' in template && template.realScale && artwork.dimensions?.width && artwork.dimensions?.height) {
      const unit = artwork.dimensions.unit || 'cm';
      const wCm = unit === 'in' ? artwork.dimensions.width * 2.54 : artwork.dimensions.width;
      const hCm = unit === 'in' ? artwork.dimensions.height * 2.54 : artwork.dimensions.height;
      const { pxPerCm, wallArea } = template.realScale;
      const widthPct = (wCm * pxPerCm / TEMPLATE_W) * 100;
      const heightPct = (hCm * pxPerCm / TEMPLATE_H) * 100;
      const leftPct = wallArea.leftPct + (wallArea.widthPct - widthPct) / 2;
      const topPct = wallArea.topPct + (wallArea.heightPct - heightPct) / 2;
      return { leftPct, topPct, widthPct, heightPct };
    }
    return template.frame;
  })();

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const artworkImage = artwork.images?.[0] || '';
  const artworkTitle = localize(artwork.title);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[990] bg-black"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[995] flex items-center justify-center p-0"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-[999] w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-all"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {/* Title overlay */}
            <div className="absolute top-6 left-6 z-[999] text-white">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 mb-1">
                {t.artwork?.viewInRoom || 'View in Room'}
              </p>
              <h3 className="text-lg sm:text-xl font-syncopate font-bold uppercase tracking-tight">
                {artworkTitle}
              </h3>
            </div>

            {/* Room scene with artwork composited in frame */}
            <div className="relative w-full h-full flex items-center justify-center px-4 sm:px-12 py-20 sm:py-24">
              <div
                className="relative shadow-2xl shadow-black/50"
                style={{
                  aspectRatio: `${TEMPLATE_W} / ${TEMPLATE_H}`,
                  width: `min(90vw, calc((100vh - 180px) * ${TEMPLATE_W / TEMPLATE_H}))`,
                  height: `min(calc(100vh - 180px), calc(90vw * ${TEMPLATE_H / TEMPLATE_W}))`,
                }}
              >
                {/* Template room photo */}
                <img
                  src={template.src}
                  alt="Living room template"
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />

                {/* User's artwork inside the frame */}
                {artworkImage && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className={`absolute ${template.fit === 'cover' ? 'overflow-hidden' : ''}`}
                    style={{
                      left: `${FRAME_INNER.leftPct}%`,
                      top: `${FRAME_INNER.topPct}%`,
                      width: `${FRAME_INNER.widthPct}%`,
                      height: `${FRAME_INNER.heightPct}%`,
                    }}
                  >
                    <img
                      src={artworkImage}
                      alt={artworkTitle}
                      className={`w-full h-full ${template.fit === 'contain' ? 'object-contain' : 'object-cover'}`}
                      draggable={false}
                      style={template.fit === 'contain' ? { filter: 'drop-shadow(2px 4px 6px rgba(0,0,0,0.25))' } : undefined}
                    />
                    {/* Subtle inner shadow for realism (only for framed templates) */}
                    {template.fit === 'cover' && (
                      <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_30px_rgba(0,0,0,0.15)]" />
                    )}
                  </motion.div>
                )}
              </div>
            </div>

            {/* Bottom hint */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-[9px] font-bold uppercase tracking-[0.3em]">
              Press ESC to close
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ViewInRoomModal;
